/**
 * Org Slack Webhook Setup — admin page
 * =====================================
 * Wraps the existing /api/org-settings/slack endpoints with a simple UI.
 * Lives at /admin/slack-setup.
 *
 * Once configured, the daily commitment-drift cron actually posts to
 * the org's Slack — until now this gate was open but unused.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SEOHead from '../../components/SEOHead';
import {
  ArrowLeft, Slack, Loader2, CheckCircle2, AlertTriangle, Send,
  Trash2, Save,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SlackSetup = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [hint, setHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/org-settings/slack`, { headers });
      setState(data);
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Could not load Slack settings' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!url.startsWith('https://hooks.slack.com/services/')) {
      setMsg({ kind: 'err', text: 'URL must start with https://hooks.slack.com/services/' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await axios.put(`${API_URL}/org-settings/slack`, { webhook_url: url, channel_hint: hint }, { headers });
      setMsg({ kind: 'ok', text: 'Saved. The daily drift cron will now post to this webhook.' });
      setUrl('');
      setHint('');
      await refresh();
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Could not save' });
    } finally {
      setSaving(false);
    }
  };

  const removeWebhook = async () => {
    if (!window.confirm('Delete the Slack webhook? The daily drift cron will stop posting to this channel.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/org-settings/slack`, { headers });
      setMsg({ kind: 'ok', text: 'Webhook removed.' });
      await refresh();
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Could not delete' });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setMsg(null);
    try {
      const { data } = await axios.post(`${API_URL}/org-settings/slack/test`, {}, { headers });
      setMsg({ kind: 'ok', text: data?.ok ? 'Test ping sent — check your channel.' : 'Slack responded but rejected the ping.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEOHead title="Slack setup · CARTIEAI" noindex />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="mb-6">
          <Link to="/admin/pilot-activation" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="slack-setup-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Pilot Activation
          </Link>
        </div>

        <div className="mb-8">
          <Badge className="bg-emerald-500/20 text-emerald-200 mb-3">Admin · Slack integration</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="slack-setup-title">
            <Slack className="w-7 h-7 inline-block mr-2 text-emerald-400" />
            Slack webhook for drift alerts
          </h1>
          <p className="text-sm sm:text-base text-slate-700">
            Once you paste your incoming-webhook URL, the daily 09:45 UTC commitment-drift cron posts alerts directly to your channel.
            We only post on high-severity drift — never on stable days.
          </p>
        </div>

        {loading && (
          <Card className="border-emerald-500/20">
            <CardContent className="pt-6 text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</CardContent>
          </Card>
        )}

        {state && state.configured && (
          <Card className="border-emerald-500/30 bg-emerald-500/10 mb-4" data-testid="slack-configured-card">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-300 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-100">Slack is configured</div>
                  <div className="text-sm text-emerald-200 mt-1">
                    <code className="text-xs bg-slate-900 px-1.5 py-0.5 rounded border border-emerald-500/30">{state.webhook_url_masked}</code>
                    {state.channel_hint && <span className="ml-2">→ {state.channel_hint}</span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Updated {state.updated_at?.split('T')[0]} by {state.updated_by}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button onClick={sendTest} disabled={testing} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="slack-test-btn">
                      {testing ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Testing…</> : <><Send className="w-3 h-3 mr-1.5" /> Send test ping</>}
                    </Button>
                    <Button onClick={removeWebhook} disabled={saving} size="sm" variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="slack-remove-btn">
                      <Trash2 className="w-3 h-3 mr-1.5" /> Remove
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-emerald-500/20" data-testid="slack-form-card">
          <CardHeader>
            <CardTitle className="text-lg">{state?.configured ? 'Replace webhook' : 'Configure webhook'}</CardTitle>
            <CardDescription className="text-slate-400">
              Create an Incoming Webhook at <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-emerald-300 underline">api.slack.com/apps</a> → pick channel → copy URL → paste below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Webhook URL</label>
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/T.../B.../..."
                  data-testid="slack-url-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Channel hint (optional)</label>
                <Input
                  type="text"
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  placeholder="#cost-alerts"
                  data-testid="slack-hint-input"
                />
              </div>
              <Button onClick={save} disabled={saving || !url} className="bg-emerald-600 hover:bg-emerald-700" data-testid="slack-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save webhook</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {msg && (
          <Card className={`mt-3 ${msg.kind === 'ok' ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-rose-500/30 bg-rose-500/10'}`}>
            <CardContent className="pt-4 text-sm flex items-center gap-2" data-testid="slack-setup-message">
              {msg.kind === 'ok' ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <AlertTriangle className="w-4 h-4 text-rose-300" />}
              <span className={msg.kind === 'ok' ? 'text-emerald-100' : 'text-rose-200'}>{msg.text}</span>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SlackSetup;
